import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format } from 'date-fns';
import {
  CalendarDays,
  Building2,
  MapPin,
  Users,
  Clock,
  Send,
  Loader2,
  Calendar,
  Check,
  PartyPopper,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const eventTypes = [
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'CONFERENCE', label: 'Conference' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'BIRTHDAY', label: 'Birthday Party' },
  { value: 'CORPORATE', label: 'Corporate Event' },
  { value: 'SOCIAL', label: 'Social Gathering' },
  { value: 'OTHER', label: 'Other' },
];

export default function GuestEventsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, guest, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [functionSpaces, setFunctionSpaces] = useState([]);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    event_type: '',
    event_date: null,
    expected_attendance: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    additional_notes: '',
  });

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      loadUpcomingEvents(selectedProperty);
      loadFunctionSpaces(selectedProperty);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (isAuthenticated && guest) {
      setInquiryForm(prev => ({
        ...prev,
        contact_name: `${guest.first_name || ''} ${guest.last_name || ''}`.trim(),
        contact_email: guest.email || '',
      }));
    }
  }, [isAuthenticated, guest]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await guestPortalApi.listProperties();
      setProperties(response.data || []);
      if (response.data?.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingEvents = async (propertyId) => {
    try {
      const response = await guestPortalApi.listUpcomingEvents(propertyId);
      setUpcomingEvents(response.data || []);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const loadFunctionSpaces = async (propertyId) => {
    try {
      const response = await guestPortalApi.listFunctionSpaces(propertyId);
      setFunctionSpaces(response.data || []);
    } catch (error) {
      console.error('Failed to load function spaces:', error);
    }
  };

  const handleInquire = (space = null) => {
    setSelectedSpace(space);
    setShowInquiryDialog(true);
  };

  const handleSubmitInquiry = async () => {
    if (!inquiryForm.event_type || !inquiryForm.event_date || !inquiryForm.contact_name || !inquiryForm.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await guestPortalApi.submitEventInquiry({
        property_id: selectedProperty,
        client_name: inquiryForm.contact_name,
        contact_email: inquiryForm.contact_email,
        contact_phone: inquiryForm.contact_phone || null,
        event_type: inquiryForm.event_type,
        event_name: selectedSpace ? `Inquiry for ${selectedSpace.name}` : null,
        event_date: format(inquiryForm.event_date, 'yyyy-MM-dd'),
        expected_attendance: parseInt(inquiryForm.expected_attendance) || null,
        space_requirements: selectedSpace?.name || null,
        special_requests: inquiryForm.additional_notes || null,
      });
      toast.success('Your inquiry has been submitted! Our events team will contact you shortly.');
      setShowInquiryDialog(false);
      setInquiryForm({
        event_type: '',
        event_date: null,
        expected_attendance: '',
        contact_name: isAuthenticated ? `${guest?.first_name || ''} ${guest?.last_name || ''}`.trim() : '',
        contact_email: isAuthenticated ? guest?.email || '' : '',
        contact_phone: '',
        additional_notes: '',
      });
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const getMaxCapacity = (space) => {
    if (!space.capacities || space.capacities.length === 0) return null;
    return Math.max(...space.capacities.map(c => c.capacity));
  };

  if (loading || authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-10 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-80" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="guest-events-page">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-background dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <CalendarDays className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-4xl font-bold">Events & Meetings</h1>
            </div>
            <p className="text-lg text-muted-foreground mb-6">
              From intimate gatherings to grand celebrations, we have the perfect 
              venue for your next event. Our experienced team will help bring your 
              vision to life.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-64">
                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => handleInquire()} data-testid="general-inquiry-btn">
                <Send className="h-4 w-4 mr-2" />
                Request Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <Tabs defaultValue="venues" className="space-y-8">
          <TabsList>
            <TabsTrigger value="venues">Our Venues ({functionSpaces.length})</TabsTrigger>
            <TabsTrigger value="events">Upcoming Events ({upcomingEvents.length})</TabsTrigger>
          </TabsList>

          {/* Venues Tab */}
          <TabsContent value="venues">
            {functionSpaces.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No venues available</p>
                  <p className="text-muted-foreground">Please check back later or select another property</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {functionSpaces.map(space => (
                  <Card 
                    key={space.id}
                    className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
                    data-testid={`venue-${space.id}`}
                  >
                    <div className="aspect-video bg-gradient-to-br from-blue-100 via-indigo-50 to-purple-100 dark:from-blue-900/30 dark:via-indigo-900/20 dark:to-purple-900/30 flex items-center justify-center">
                      <Building2 className="h-16 w-16 text-blue-400/40" />
                    </div>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{space.name}</CardTitle>
                      {space.description && (
                        <CardDescription className="line-clamp-2">
                          {space.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {space.area_sqm && (
                          <Badge variant="outline">{space.area_sqm} sqm</Badge>
                        )}
                        {space.location && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {space.location}
                          </Badge>
                        )}
                        {getMaxCapacity(space) && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Up to {getMaxCapacity(space)} guests
                          </Badge>
                        )}
                      </div>
                      
                      {space.capacities?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-muted-foreground mb-2">Setup Options:</p>
                          <div className="flex flex-wrap gap-1">
                            {space.capacities.map((cap, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {cap.setup_type}: {cap.capacity}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <Button 
                        className="w-full" 
                        onClick={() => handleInquire(space)}
                        data-testid={`inquire-${space.id}`}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Inquire About This Venue
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {upcomingEvents.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <PartyPopper className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No upcoming events</p>
                  <p className="text-muted-foreground">Check back later for public events</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {upcomingEvents.map(event => (
                  <Card key={event.id} className="overflow-hidden" data-testid={`event-${event.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2">{event.event_type}</Badge>
                          <CardTitle>{event.event_name}</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {format(new Date(event.event_date), 'EEEE, MMMM d, yyyy')}
                            {event.event_end_date && event.event_end_date !== event.event_date && (
                              <> - {format(new Date(event.event_end_date), 'MMMM d, yyyy')}</>
                            )}
                          </span>
                        </div>
                        {event.expected_attendance && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>Expected attendance: {event.expected_attendance}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Inquiry Dialog */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Event Inquiry</DialogTitle>
            <DialogDescription>
              {selectedSpace 
                ? `Inquire about ${selectedSpace.name}`
                : 'Tell us about your event and we\'ll help you find the perfect venue'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Type *</Label>
                <Select 
                  value={inquiryForm.event_type} 
                  onValueChange={(v) => setInquiryForm(prev => ({...prev, event_type: v}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expected Guests</Label>
                <Input
                  type="number"
                  placeholder="Number of guests"
                  value={inquiryForm.expected_attendance}
                  onChange={(e) => setInquiryForm(prev => ({...prev, expected_attendance: e.target.value}))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Event Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <Calendar className="h-4 w-4 mr-2" />
                    {inquiryForm.event_date 
                      ? format(inquiryForm.event_date, 'EEEE, MMMM d, yyyy')
                      : 'Select date'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={inquiryForm.event_date}
                    onSelect={(date) => setInquiryForm(prev => ({...prev, event_date: date}))}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  placeholder="Full name"
                  value={inquiryForm.contact_name}
                  onChange={(e) => setInquiryForm(prev => ({...prev, contact_name: e.target.value}))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={inquiryForm.contact_email}
                  onChange={(e) => setInquiryForm(prev => ({...prev, contact_email: e.target.value}))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                type="tel"
                placeholder="+251 9XX XXX XXXX"
                value={inquiryForm.contact_phone}
                onChange={(e) => setInquiryForm(prev => ({...prev, contact_phone: e.target.value}))}
              />
            </div>

            <div className="space-y-2">
              <Label>Additional Details</Label>
              <Textarea
                placeholder="Tell us more about your event (catering needs, special requirements, etc.)"
                rows={3}
                value={inquiryForm.additional_notes}
                onChange={(e) => setInquiryForm(prev => ({...prev, additional_notes: e.target.value}))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInquiryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitInquiry} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Inquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
